import type { CpvCodeDto } from "../../autogen/bff-api";

export type CpvNode = {
  code: string;
  nameEn: string;
  nameNb: string;
  depth: number;
  children: CpvNode[];
};

const CODE_PATTERN = /^\d{8}$/;

function assertValid(code: string): void {
  if (!CODE_PATTERN.test(code)) {
    throw new Error(`Invalid CPV code (must be 8 digits): ${code}`);
  }
}

function lastSignificantPosition(code: string): number {
  for (let i = 7; i >= 0; i--) {
    if (code[i] !== "0") return i;
  }
  return -1;
}

function hasPlaceInHierarchy(code: string): boolean {
  return CODE_PATTERN.test(code) && lastSignificantPosition(code) >= 0;
}

export function levelOf(code: string): number {
  assertValid(code);
  const pos = lastSignificantPosition(code);
  if (pos < 0) {
    throw new Error(`Code has no significant digits: ${code}`);
  }
  return pos === 0 ? 1 : pos;
}

export function parentOf(code: string): string | null {
  assertValid(code);
  const pos = lastSignificantPosition(code);
  if (pos <= 1) return null;
  return code.substring(0, pos) + "0" + code.substring(pos + 1);
}

export function nearestKnownAncestor(
  code: string,
  known: ReadonlySet<string>,
): string | null {
  for (let ancestor = parentOf(code); ancestor !== null; ancestor = parentOf(ancestor)) {
    if (known.has(ancestor)) return ancestor;
  }
  return null;
}

export function isAncestorOf(ancestor: string, descendant: string): boolean {
  if (ancestor === descendant) return false;
  const ancestorLevel = levelOf(ancestor);
  const descendantLevel = levelOf(descendant);
  if (ancestorLevel >= descendantLevel) return false;
  const prefixLen = ancestorLevel + 1;
  return descendant.substring(0, prefixLen) === ancestor.substring(0, prefixLen);
}

export function indexByCode(codes: CpvCodeDto[]): Record<string, CpvCodeDto> {
  const result: Record<string, CpvCodeDto> = {};
  for (const code of codes) result[code.code] = code;
  return result;
}

export function buildTree(codes: CpvCodeDto[]): CpvNode[] {
  const byCode = new Map<string, CpvNode>();
  for (const c of codes) {
    if (!hasPlaceInHierarchy(c.code)) continue;
    byCode.set(c.code, {
      code: c.code,
      nameEn: c.nameEn,
      nameNb: c.nameNb,
      depth: 0,
      children: [],
    });
  }
  const known = new Set(byCode.keys());
  const roots: CpvNode[] = [];
  byCode.forEach((node) => {
    const parentCode = nearestKnownAncestor(node.code, known);
    const parent = parentCode != null ? byCode.get(parentCode) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  const sortDeep = (nodes: CpvNode[], depth: number): void => {
    nodes.sort((a, b) => a.code.localeCompare(b.code));
    for (const n of nodes) {
      n.depth = depth;
      sortDeep(n.children, depth + 1);
    }
  };
  sortDeep(roots, 0);
  return roots;
}

export type VisibleCpvNode = {
  node: CpvNode;
  /** Index of the row holding this one, for ArrowLeft out of a collapsed node. */
  parentIndex: number | null;
  /** 1-based place among its siblings, and how many there are — a flat tree has to say so itself. */
  posInSet: number;
  setSize: number;
};

/**
 * The rows a tree draws for a given expansion, in the order they appear on screen. The keyboard
 * works on this list: nesting is carried by `depth` and `parentIndex` rather than by the DOM, so
 * moving between rows is one step along an array.
 */
export function flattenVisible(
  nodes: CpvNode[],
  expandedIds: ReadonlySet<string>,
): VisibleCpvNode[] {
  const out: VisibleCpvNode[] = [];
  const walk = (level: CpvNode[], parentIndex: number | null): void => {
    level.forEach((node, position) => {
      const index = out.length;
      out.push({ node, parentIndex, posInSet: position + 1, setSize: level.length });
      if (node.children.length > 0 && expandedIds.has(node.code)) walk(node.children, index);
    });
  };
  walk(nodes, null);
  return out;
}
